import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from 'styled-components';
import { StyledGroupedToolsButtons, StyledGroupedToolsButtonsContainer } from './styled/StyledGroupedTools';
import { CreateButton, CreateOptionsButton, CreateToggleButton } from './ToolbarButtons';

export const GroupedTools = ({ tools, setGroupedTools, groupedToolsXPosition, groupedToolsYPosition, groupedToolsButtonTopPosition, currentTool, selectedElement, handlerSelectTool }) => {
  const containerRef = useRef();
  const [compactPosition, setCompactPosition] = useState(null);
  const theme = useTheme();
  const desktopBreakpoint = Number.parseInt(theme?.sizes?.desktop || '1280', 10);

  useLayoutEffect(() => {
    const updatePosition = () => {
      const panel = containerRef.current;
      if (!panel || window.innerWidth > desktopBreakpoint) {
        setCompactPosition(null);
        return;
      }

      const { width, height } = panel.getBoundingClientRect();
      const edgePadding = 8;
      const topOfButton = groupedToolsButtonTopPosition ?? groupedToolsYPosition ?? edgePadding;
      const bottomOfButton = groupedToolsYPosition ?? topOfButton;
      const belowTop = bottomOfButton + 4;
      const spaceBelow = Math.max(0, window.innerHeight - edgePadding - belowTop);
      const spaceAbove = Math.max(0, topOfButton - edgePadding - 4);
      const openBelow = height <= spaceBelow || (height > spaceAbove && spaceBelow >= spaceAbove);
      const maxHeight = openBelow ? spaceBelow : spaceAbove;
      const panelHeight = Math.min(height, maxHeight);
      const top = openBelow ? belowTop : Math.max(edgePadding, topOfButton - panelHeight - 4);
      const anchorLeft = groupedToolsXPosition ?? edgePadding;
      const left = Math.max(edgePadding, Math.min(anchorLeft, window.innerWidth - width - edgePadding));

      setCompactPosition({ top, left, maxHeight });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [desktopBreakpoint, groupedToolsButtonTopPosition, groupedToolsXPosition, groupedToolsYPosition, tools]);

  const handleClickOutside = (event) => {
    const isInsideToolbarDropdown = event.target.closest?.('[data-toolbar-dropdown-menu="true"]');
    const isGroupedToolsTrigger = event.target.closest?.('[data-toolbar-group-trigger="true"]');
    if (
      containerRef.current
      && !containerRef.current.contains(event.target)
      && !isInsideToolbarDropdown
      && !isGroupedToolsTrigger
    ) {
      setGroupedTools(null);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  return typeof document !== 'undefined' && createPortal((
    <StyledGroupedToolsButtonsContainer>
      <StyledGroupedToolsButtons
        groupedToolsXPosition={groupedToolsXPosition}
        groupedToolsYPosition={groupedToolsYPosition}
        ref={containerRef}
        style={compactPosition || undefined}
      >
        {tools.map((tool, i) => {
          switch (tool.type) {
            case 'button':
              return (
                <CreateButton
                  key={`${tool.name}${i.toString()}`}
                  tool={tool}
                  currentTool={currentTool}
                  handlerSelectTool={handlerSelectTool}
                />
              );
            case 'toggle':
              return (
                <CreateToggleButton
                  key={`${tool.name}${i.toString()}`}
                  tool={tool}
                  currentTool={currentTool}
                  selectedElement={selectedElement}
                  handlerSelectTool={handlerSelectTool}
                />
              );
            case 'options':
              return (
                <CreateOptionsButton
                  key={`${tool.name}${i.toString()}`}
                  tool={tool}
                  currentTool={currentTool}
                  handlerSelectTool={handlerSelectTool}
                />
              );
            default:
              return <>{console.error('tool type is not correct')}</>;
          }
        })}
      </StyledGroupedToolsButtons>
    </StyledGroupedToolsButtonsContainer>
  ), document.body);
};
export default GroupedTools;
